/**
 * The empirical (68–95–99.7) rule and its distribution-free cousin, Chebyshev's inequality.
 *
 * For normally distributed data about 68%, 95% and 99.7% of values lie within 1, 2 and 3
 * standard deviations of the mean. The exact normal shares are 2Φ(k) − 1 =
 * 68.27%, 95.45% and 99.73%. Chebyshev's inequality holds for any distribution with a
 * finite variance: at least 1 − 1 ÷ k² of the values lie within k standard deviations
 * (k > 1) — 0%, 75% and 88.89% for k = 1, 2, 3.
 */

import { normalCdf } from "@/lib/stats/distributions";

export const RULE_PERCENT: Readonly<Record<number, number>> = {
  1: 68,
  2: 95,
  3: 99.7,
};

export const STANDARD_KS: readonly number[] = [1, 2, 3];

const EPSILON = 1e-9;

/** Exact share of a normal distribution within k standard deviations, 0–1. */
export function normalWithin(k: number): number {
  return 2 * normalCdf(k) - 1;
}

/** Chebyshev's guaranteed minimum share within k standard deviations, 0–1. */
export function chebyshevMinimum(k: number): number {
  return k > 1 ? 1 - 1 / (k * k) : 0;
}

export interface RuleRow {
  k: number;
  lower: number;
  upper: number;
  /** 68, 95 or 99.7 for k = 1, 2, 3; null for a custom k. */
  rulePercent: number | null;
  normal: number;
  chebyshev: number;
  /** n × exact normal share, when a sample size is given. */
  expected: number | null;
  /** How many of the pasted values lie within k SD, when data are given. */
  actualCount: number | null;
  actualShare: number | null;
}

export interface DataSummary {
  n: number;
  mean: number;
  /** Sample standard deviation, n − 1. */
  sd: number;
}

export function summarise(values: readonly number[]): DataSummary | null {
  const n = values.length;
  if (n < 2) {
    return null;
  }
  const mean = values.reduce((total, value) => total + value, 0) / n;
  const sumSquares = values.reduce(
    (total, value) => total + (value - mean) ** 2,
    0
  );
  const sd = Math.sqrt(sumSquares / (n - 1));
  return sd > 0 ? { n, mean, sd } : null;
}

export function countWithin(
  values: readonly number[],
  mean: number,
  sd: number,
  k: number
): number {
  return values.filter((value) => Math.abs(value - mean) <= k * sd + EPSILON)
    .length;
}

export function ruleRows(
  mean: number,
  sd: number,
  ks: readonly number[],
  options: { n?: number | null; data?: readonly number[] } = {}
): RuleRow[] {
  const data = options.data ?? [];
  return ks.map((k) => {
    const normal = normalWithin(k);
    const count = data.length > 0 ? countWithin(data, mean, sd, k) : null;
    return {
      k,
      lower: mean - k * sd,
      upper: mean + k * sd,
      rulePercent: RULE_PERCENT[k] ?? null,
      normal,
      chebyshev: chebyshevMinimum(k),
      expected: options.n ? options.n * normal : null,
      actualCount: count,
      actualShare: count === null ? null : count / data.length,
    };
  });
}

/** 1, 2, 3 plus the reader's own k when it is positive and not already there. */
export function withCustomK(k: number | null): readonly number[] {
  if (k === null || k <= 0 || STANDARD_KS.includes(k)) {
    return STANDARD_KS;
  }
  return [...STANDARD_KS, k];
}

/** Whether pasted data sit close to 68% within 1 SD and 95% within 2 SD. */
export function closeToRule(oneSd: number, twoSd: number): boolean {
  return Math.abs(oneSd - 0.68) < 0.07 && Math.abs(twoSd - 0.95) < 0.04;
}
